import React from 'react';
import FormComponent from '../components/formComponent';
import { Redirect } from 'react-router-dom';
import StepManager from '../components/stepManager';
import { renderReport } from '../components/elementList'
import UiGetDashboard from './uiGetDashboard';

let documentation = {
    title: "Add Customer",
    subtitle: 'Adds a new customer to Thingsboard Acuadori',
    content: <p>Adds a new customer to Thingsboard for each type of customer chosen on <i>Add Customer</i> step.
            The customers are added when submitting on the <i>Set Customer Contact</i> step.
            A default dashboard must be set and will be associated with all the customers created.
            <p><span className="text-warning">WARNING:</span> Be aware that at least a dashboard should be present before choosing one.</p>
        <p><span className="text-danger">Be aware if you want to delete a customer.</span> Remember 
            to remove the customer on each associated dashboard list, else you will not associate or disassociate 
            a customer anymore from that dashboard on Thingsboard.</p>
    </p>,

    steps: [
        {
            id: 0,
            title: 'Add Customer Info',
            brief: 'Choose customer Name and account types.',
            content: 'One thingsboard customer will be created for each enabled account.'
        },
        {
            id: 1,
            title: 'Set Customer Contacts',
            brief: 'Customer Contacts are filled in this step.',
            content: 'Customer data will be visible on thingsboard.None is required, but setting address is recommended.'
        },
        {
            id: 2,
            title: 'Choose Default Dashboard',
            brief: 'Choose a default dashboard for all Acuadori customer types.',
            content: <p>Acuadori dashboard is chosen if present.
            <p className='text-warning'>At least one dashboard must be present in Thingsboard to accomplish this step</p></p>

        },
        {
            id: 3,
            title: 'Add Customer default User',
            brief: 'One default user is created and activated for each customer-type.',
            content: `It is possible to customize the email. 
            Once created the chosen default dashboard is assigned and visualized as user accesses to Thingsboard.`

        }
    ]
};


class UiAddCustomer extends React.Component {

    static defaultProps = {
        title: '',
        customerTypes: [
            { name: ' Agronomist', fieldPre: 'FA ', fieldPost: ' ag', devicePre: 'DA ', devicePost: ' ag' },
            { name: ' Operator', fieldPre: 'FO ', fieldPost: ' op', devicePre: 'DO ', devicePost: ' op' }
        ]
    };

    constructor(props) {
        super(props);

        this.customerService = this.props.services.customer;
        this.userService = this.props.services.user;
        this.dashboardService = this.props.services.dashboard;

        this.stepManagerRef = React.createRef();
        this.state = {
            customer: {
                title: '',
                types: [],
                dashboard: null
            },
            createdCustomers: [],
            createdUsers: [],
        };
    }

    documentation = (title) => {
        if (!title) { return documentation; }

        let doc = documentation;
        documentation.title = title;
        return doc;
    }

    stepManagerAdvance = () => {
        const element = this.stepManagerRef.current;
        element.advance();
    };

    stepManagerGoBack = () => {
        const element = this.stepManagerRef.current;
        element.goBack();
    }

    renderAlert = (message, variant) => {
        this.stepManagerRef.current.showAlert(message, variant);
    };
    handleSubmitError = (error, callback) => {
        let message = 'Error: ' + error.message;
        this.renderAlert(message, 'warning');
        if (callback) {
            callback();
        }
    };

    reportAllComponent = () => {
        return this.reportAddCustomer().concat(this.reportGeneratedCustomers());
    }

    reportAddCustomer = () => {
        let { customer } = this.state;
        let data = {
            Customer: customer.title,
            'Customer Types': customer.types
        };
        return data;
    }

    reportGeneratedCustomers = () => {
        let { customer, createdCustomers } = this.state;
        let data = { Customer: customer.title, 'Accounts': [] };

        createdCustomers.forEach(serverCustomer => {
            data['Accounts'].push(serverCustomer.title);
            data.Description = serverCustomer.additionalInfo.description;
        });

        return data
    }
    reportGeneratedUsers = () => {
        let { createdUsers } = this.state;
        let data = {};

        createdUsers.forEach(user => {
            data[user.firstName] = user.email;
        });

        return data
    }

    addCustomer = (data, i) => {
        let { types } = this.state.customer;
        let { createdCustomers } = this.state;
        return this.customerService.addCustomer(data).then((response) => {
            createdCustomers.push(response);

            if (i === (types.length - 1)) { //is last
                this.setState({ createdCustomers: createdCustomers }, this.stepManagerAdvance);
            }
            else {
                this.setState({ createdCustomers: createdCustomers });
            }
        });
        // }).catch(this.handleSubmitError);
    }

    handleValidate = (values) => {
        let { customerTypes } = this.props;
        let errors = {};
        if (!values.title) { errors.title = 'Required'; }
        else {
            let atLeastOne = false;
            let atLeastOneId = customerTypes[0].name;
            customerTypes.forEach((type) => {
                if (values[type.name]) {
                    atLeastOne = true;
                }
            });
            if (!atLeastOne && atLeastOneId) { errors[atLeastOneId] = 'Check at least one Customer Type!'; }
        }
        return errors;
    }

    handleAddCustomer = (values) => {
        let { customerTypes } = this.props;
        let data = { title: values.title, types: [] };
        customerTypes.forEach(type => {
            if (values.hasOwnProperty(type.name)) {
                if (true === values[type.name]) {
                    data.types.push(type.name);
                }
            }
        });

        return this.setState({ customer: { title: data.title, types: data.types } }, this.stepManagerAdvance);
    }


    handleCustomerContact = (values) => {
        const { types, title } = this.state.customer;
        const customerRequests = types.map((type) => {
            const customerData = {
                ...values,
                title: title + type,
                additionalInfo: {
                    description: values.description,
                    type: type,
                    customerTitle: title
                }
            };

            return this.customerService.addCustomer(customerData);
        });

        return Promise.all(customerRequests)
            .then((createdCustomers) => {
                this.setState({ createdCustomers: createdCustomers }, this.stepManagerAdvance);
                return createdCustomers;
            })
            .catch((error) => this.handleSubmitError(error, this.stepManagerGoBack));
    }

    renderAddCustomer = () => {
        let { customerTypes } = this.props;

        let inputs = [{ key: 'title', type: 'text', inputType: 'input', placeholder: 'Wine Producer Name', label: 'Name', required: true }];
        let initials = {};
        customerTypes.forEach(type => {
            inputs.push({ key: type.name, type: 'checkbox', inputType: 'checkbox', label: `Enable ${type.name} account` });
            initials[type.name] = true;
        });

        return (
            <div>
                <FormComponent
                    initialValues={{ title: '', ...initials }}
                    validateFunc={this.handleValidate}
                    submitFunc={this.handleAddCustomer}
                    inputs={inputs}
                />
            </div>
        );
    }

    renderCustomerContact = () => {
        return (
            <div>
                <FormComponent
                    initialValues={{
                        description: "The best wine producer", country: '',
                        city: '', state: '', zip: '', address: '', phone: '', email: ''
                    }}
                    submitFunc={this.handleCustomerContact}
                    inputs={[
                        { key: 'description', type: 'text', inputType: 'input', placeholder: 'Description', label: 'Description' },
                        { key: 'country', type: 'text', inputType: 'input', placeholder: 'the country where the customer operates', label: 'Country' },
                        { key: 'city', type: 'text', inputType: 'input', placeholder: 'the city', label: 'city' },
                        { key: 'state', type: 'text', inputType: 'input', placeholder: 'the state', label: 'state' },
                        { key: 'zip', type: 'text', inputType: 'input', placeholder: 'the zip code', label: 'zip' },
                        { key: 'address', type: 'text', inputType: 'input', placeholder: 'street name and address number', label: 'address' },
                        { key: 'phone', type: 'text', inputType: 'input', placeholder: '+39 123 1234567', label: 'phone' },
                        { key: 'email', type: 'email', inputType: 'input', placeholder: 'user@customer.com', label: 'email' },
                    ]}
                />
            </div>
        );
    }


    // getCustomerType = (customer) => {
    //     let { types } = this.state.customer;
    //     let type;
    //     types.forEach(t => { 
    //         if (customer.title.indexOf(t) >= 0) { type = t; } 
    //     });
    //     return type;
    // }

    handleAddUser = (values) => {
        let { createdCustomers } = this.state;

        let { types, dashboard } = this.state.customer;
        if (!dashboard || !dashboard.id || !dashboard.id.id) {
            return Promise.reject(Error('Default dashboard data unavailable'))
                .catch(this.handleSubmitError);
        }

        let _promise = createdCustomers.reduce((sequence, customer) => {
            return sequence.then((createdUsers) => {
                const type = types.find(t => customer.title.indexOf(t) >= 0);
                if (!type) {
                    throw Error('Customer type not found');
                }

                const email = values[`${type} Email`];
                const additionalInfo = {
                    "defaultDashboardFullscreen": false,
                    "defaultDashboardId": dashboard.id.id
                };
                const userData = {
                    firstName: values[`${type} Name`],
                    additionalInfo: additionalInfo
                };

                return this.customerService.getCustomerById(customer.id.id)
                    .then((latestCustomer) => this.customerService.updateCustomer({
                        ...latestCustomer,
                        email: email
                    }))
                    .then((updatedCustomer) =>
                        this.userService.addUser(updatedCustomer.id.id, email, userData)
                    )
                    .then((createdUser) => [...createdUsers, createdUser]);
            });
        }, Promise.resolve([])).then((createdUsers) => {
            this.setState({ createdUsers: createdUsers });
            return createdUsers;
        }).then((users) => {
            /* Activate Users */
            let activationSequence = [];
            users.forEach(user => {
                let activationPromise = this.userService.getActivationLink(user.id.id).then(
                    (response) => {
                        let url = new URL(response);
                        let search = new URLSearchParams(url.search);
                        return search.get('activateToken');
                    }
                ).then((token) => {
                    return this.userService.activateUser(token, 'Abinsula2020');
                }).catch(this.handleSubmitError);
                activationSequence.push(activationPromise);
            });

            return Promise.all(activationSequence)
                .then(this.stepManagerAdvance)
                .catch(this.handleSubmitError);

        }).catch(this.handleSubmitError);

        return _promise;
    }
    renderAddDefaultUser = () => {
        let { types } = this.state.customer;
        let { createdCustomers } = this.state;

        let inputs = [];
        let initials = {};
        let customerName = this.state.customer.title;
        createdCustomers.forEach(customer => {

            let type;

            types.forEach(t => { if (customer.title.indexOf(t) >= 0) { type = t; } });

            if (type) {
                inputs.push({
                    key: `${type} Name`, type: 'text', inputType: 'input', placeholder: `${customer.title} ${type} Name`,
                    label: `${type} Name`, required: true
                });
                inputs.push({
                    key: `${type} Email`, type: 'email', inputType: 'input', placeholder: `${customer.title} ${type} Name`,
                    label: `${type} Email`, required: true
                });
                initials[`${type} Name`] = `Default ${type.trim()}`;
                let emailEnd = customerName
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '');

                emailEnd = emailEnd || 'customer';
                initials[`${type} Email`] = `${type.trim().toLowerCase()}@${emailEnd}.com`;
            }
        });

        return (
            <div>
                <FormComponent
                    initialValues={{ ...initials }}
                    //validateFunc={this.handleValidate}
                    submitFunc={this.handleAddUser}
                    inputs={inputs}
                />
            </div>
        );
    }

    renderSetDashboard = () => {
        let { customer, createdCustomers } = this.state;

        let instances = createdCustomers.map(customerItem => {
            return { id: customerItem.id.id, name: customerItem.name };
        });

        return (
            <UiGetDashboard
                defaultDashboard={'67b1d580-f298-11ea-9bda-dbe1f0ac55e3'} //Questo è ID di una dashboard TB3 hardcoded, andrà modificato il codice
                services={{
                    dashboard: this.props.services.dashboard,
                    customer: this.props.services.customer
                }}
                customers={{ name: customer.title, instances: instances }}
                finished={(data) => {
                    customer.dashboard = data.dashboard;
                    this.setState({ customer: customer }, this.stepManagerAdvance);
                }} />
        );
    }

    reportCustomerDashboard = () => {
        return { Dashboard: this.state.customer.dashboard.name };
    }

    render() {
        let { finished } = this.state;
        let documentation = this.documentation(this.props.title);

        return (
            <div className="container-fluid">
                {finished && <Redirect to={this.props.nextRoute} />}
                {!finished && <StepManager ref={this.stepManagerRef}
                    documentation={documentation}
                    alert={{ active: true }}
                    startStepId={0} endStepId={3}
                    onFinished={() => {
                        //window.open( this.props.links.thingsboardRoutes.dashboards );
                        this.setState({ finished: true },
                            this.stepManagerAdvance());
                    }}
                    stepList={[
                        {
                            stepId: 0, stepName: documentation.steps[0].title, formValue: this.renderAddCustomer,
                            dataValue: () => renderReport(this.reportAddCustomer()),
                            documentation: documentation.steps[0]
                        },
                        {
                            stepId: 1, stepName: documentation.steps[1].title, formValue: this.renderCustomerContact,
                            dataValue: () => renderReport(this.reportGeneratedCustomers()),
                            documentation: documentation.steps[1]
                        },
                        {
                            stepId: 2, stepName: documentation.steps[2].title, formValue: this.renderSetDashboard,
                            dataValue: () => renderReport(this.reportCustomerDashboard()),
                            documentation: documentation.steps[2]
                        },
                        {
                            stepId: 3, stepName: documentation.steps[3].title, formValue: this.renderAddDefaultUser,
                            dataValue: () => renderReport(this.reportGeneratedUsers()),
                            documentation: documentation.steps[3]
                        }
                    ]}
                />}
            </div>
        );
    }
}

export default UiAddCustomer;
export { UiAddCustomer, documentation }